// Import libraries
import { lazy, Suspense, useEffect, useState } from "react";
import { Route, Routes, useLocation } from "react-router-dom";
import { ConfigProvider } from "antd";
import { useDispatch } from "react-redux";
import antdTheme from "./theme/antdTheme";

// Import site layouts
import { LoginLayoutRoute } from "./layouts/LoginLayout";
import { MainLayoutRoute } from "./layouts/MainLayout";
import { EmptyLayoutRoute } from "./layouts/EmptyLayout";
import { ProfileLayoutRoute } from "./layouts/ProfileLayout";
import { SettingsLayoutRoute } from "./layouts/SettingsLayout";
import { UICustomizationLayoutRoute } from "./layouts/UICustomizationLayout";
import { RoutePath } from "./constants/SitePaths";
import NotFound from "./routes/NotFound";
import RootRedirect from "./routes/RootRedirect";

// Import drag and drop css (still needed for other components using Dragula)
import "dragula/dist/dragula.css";

import { Auth } from "./services/Auth";
import { Utils } from "./services/Utils";
import { getUserData } from "./actions/UserActions";
import { LanguageService } from "./services/LanguageService";

// Account

// Import site routs
const Sso = lazy(() => import("./routes/Sso"));
const Projects = lazy(() => import("./routes/project/Projects"));
const CreatePage = lazy(() => import("./routes/CreatePage"));
const Overview = lazy(() => import("./routes/settings/Overview"));
const CreateProject = lazy(() => import("./routes/project/CreateProject"));

// Import settings routes
const Currencies = lazy(() => import("./routes/settings/Currencies"));
const Languages = lazy(() => import("./routes/settings/Languages"));
const Customize = lazy(() => import("./routes/ui/Customize"));
const AgeRestriction = lazy(() => import("./routes/settings/general-settings/AgeRestriction"));
const Licenses = lazy(() => import("./routes/settings/general-settings/Licenses"));
const ReferralFriend = lazy(() => import("./routes/settings/general-settings/ReferralFriend"));
const Products = lazy(() => import("./routes/settings/Products"));

const ThirdPartyIntegrations = lazy(() => import("./routes/settings/ThirdPartyIntegrations"));
const CustomScripts = lazy(() => import("./routes/settings/custom-scripts/CustomScripts"));
const AddOns = lazy(() => import("./routes/settings/AddOns"));
const SeoSetiings = lazy(() => import("./routes/settings/seo/SeoSettings"));
const SeoSocial = lazy(() => import("./routes/settings/seo/seoSocial/SeoSocial"));
// const SeoSchema = lazy(() => import('./routes/settings/seo/SeoSchema'))
const SeoSitemap = lazy(() => import("./routes/settings/seo/SeoSitemap"));
const SeoRobots = lazy(() => import("./routes/settings/seo/robots/SeoRobots"));
const Domains = lazy(() => import("./routes/settings/domains/Domains"));
const AddDomain = lazy(() => import("./routes/settings/domains/AddDomain"));
const DomainInfo = lazy(() => import("./routes/settings/domains/DomainInfo"));
const DevTools = lazy(() => import("./routes/settings/DevTools"));

// Account
const Login = lazy(() => import("./routes/account/Login"));
const AccountVerification = lazy(() => import("./routes/account/verification"));
const ForgotPassword = lazy(() => import("./routes/account/ForgotPassword"));
const Profile = lazy(() => import("./components/profile/Profile"));
const Roles = lazy(() => import("./components/profile/roles/Roles"));
const AddRole = lazy(() => import("./components/profile/roles/AddRole"));
const EditRole = lazy(() => import("./components/profile/roles/EditRole"));
const VIewRole = lazy(() => import("./components/profile/roles/ViewRole"));
const Users = lazy(() => import("./components/profile/users/Users"));
const Requests = lazy(() => import("./components/profile/requests/Requests"));
const AddEditUser = lazy(() => import("./components/profile/users/AddEditUser"));
const UserDetails = lazy(() => import("./components/profile/users/UserDetails"));
const SetPassword = lazy(() => import("./routes/account/SetPassword"));
const Companies = lazy(() => import("./components/profile/companies/Companies"));
const ResetPassword = () => (
	<Suspense fallback={null}>
		<SetPassword isReset />
	</Suspense>
);

/** The main class of DigiBuilder, where discribed the routing of pages */
function App() {
	const dispatch = useDispatch();
	const location = useLocation();
	const [isLoading, setLoading] = useState(true);
	const [userInitialized, setUserInitialized] = useState(false);

	useEffect(() => {
		let cancelled = false;
		async function initializeUser() {
			try {
				const success = await dispatch(getUserData());
				if (!cancelled) setUserInitialized(success === true);
			} catch {
				if (!cancelled) setUserInitialized(false);
			} finally {
				if (!cancelled) setLoading(false);
			}
		}
		if (Auth?.isAuthenticated() && location.pathname !== RoutePath.SSO) {
			initializeUser();
		} else {
			setLoading(false);
		}
		checkLanguage();
		return () => {
			cancelled = true;
		};
	}, []);

	function checkLanguage() {
		const { language } = Utils.parseQuery(location.search);
		LanguageService.changeLanguage(dispatch, language);
	}

	return isLoading ? null : (
		<ConfigProvider theme={antdTheme}>
			<Routes>
				<Route path={RoutePath.DEFAULT} element={<RootRedirect userInitialized={userInitialized} />} />

				<Route path={RoutePath.LOGIN} element={<LoginLayoutRoute component={Login} />} />
				<Route path={`${RoutePath.SET_PASSWORD}/:hash`} element={<LoginLayoutRoute component={SetPassword} />} />
				<Route path={`${RoutePath.RESET_PASSWORD}/:hash`} element={<LoginLayoutRoute component={ResetPassword} />} />
				<Route path={RoutePath.ACCOUNT_VERIFICATION} element={<LoginLayoutRoute component={AccountVerification} />} />
				<Route path={RoutePath.FORGOT_PASSWORD} element={<LoginLayoutRoute component={ForgotPassword} />} />

				{/* Main Layout */}
				<Route
					path={RoutePath.CREATE_PROJECT}
					element={
						<MainLayoutRoute component={CreateProject} permissions={{ name: "general.generalProject", type: "add" }} />
					}
				/>
				<Route path={RoutePath.PROJECTS} element={<MainLayoutRoute component={Projects} />} />
				<Route path={RoutePath.CREATE_PAGE} element={<MainLayoutRoute component={CreatePage} />} />

				{/* Profile Layout */}
				<Route
					path={RoutePath.PROFILE}
					element={
						<ProfileLayoutRoute
							component={Profile}
							permissions={{ name: "userManagement.userMyProfile", type: "view" }}
						/>
					}
				/>
				<Route
					path={RoutePath.ROLES}
					element={
						<ProfileLayoutRoute component={Roles} permissions={{ name: "userManagement.userRoles", type: "view" }} />
					}
				/>
				<Route
					path={RoutePath.ADD_ROLES}
					element={
						<ProfileLayoutRoute component={AddRole} permissions={{ name: "userManagement.userRoles", type: "add" }} />
					}
				/>
				<Route
					path={`${RoutePath.EDIT_ROLE}/:id`}
					element={
						<ProfileLayoutRoute component={EditRole} permissions={{ name: "userManagement.userRoles", type: "edit" }} />
					}
				/>
				<Route
					path={`${RoutePath.VIEW_ROLE}/:id`}
					element={
						<ProfileLayoutRoute component={VIewRole} permissions={{ name: "userManagement.userRoles", type: "view" }} />
					}
				/>
				<Route
					path={RoutePath.USERS}
					element={
						<ProfileLayoutRoute component={Users} permissions={{ name: "userManagement.userUsers", type: "view" }} />
					}
				/>
				<Route
					path={RoutePath.ADD_USER}
					element={
						<ProfileLayoutRoute
							component={AddEditUser}
							permissions={{ name: "userManagement.userUsers", type: "add" }}
						/>
					}
				/>
				<Route
					path={`${RoutePath.USER_DETAILS}/:id`}
					element={
						<ProfileLayoutRoute
							component={UserDetails}
							permissions={{ name: "userManagement.userUsers", type: "view" }}
						/>
					}
				/>
				<Route
					path={`${RoutePath.EDIT_USER}/:id`}
					element={
						<ProfileLayoutRoute
							component={AddEditUser}
							permissions={{ name: "userManagement.userUsers", type: "edit" }}
						/>
					}
				/>
				<Route
					path={RoutePath.COMPANIES}
					element={
						<ProfileLayoutRoute
							component={Companies}
							permissions={{ name: "SuperAdmin", type: "view", withRedirect: true }}
						/>
					}
				/>
				<Route
					path={RoutePath.REQUESTS}
					element={
						<ProfileLayoutRoute component={Requests} permissions={{ name: "userManagement.userUsers", type: "view" }} />
					}
				/>

				{/* Settings Layout */}
				<Route
					path={`${RoutePath.SETTINGS_OVERVIEW}/:id`}
					element={
						<SettingsLayoutRoute component={Overview} permissions={{ name: "page.projectSettings", type: "view" }} />
					}
				/>
				<Route
					path={`${RoutePath.SETTINGS_LANGUAGES}/:id`}
					element={
						<SettingsLayoutRoute
							component={Languages}
							permissions={{ name: "projectSettings.settingsLanguages", type: "view" }}
						/>
					}
				/>
				<Route
					path={`${RoutePath.SETTINGS_CURRENCIES}/:id`}
					element={
						<SettingsLayoutRoute
							component={Currencies}
							permissions={{ name: "projectSettings.settingsCurrencies", type: "view" }}
						/>
					}
				/>

				{/* <Route path={`${RoutePath.SETTINGS_PAYMENTS}/:id`} element={<SettingsLayoutRoute component={Payments} permissions={{ name: 'projectSettings.settingsPayments', type: 'view' }} />} /> */}
				<Route
					path={`${RoutePath.SETTINGS_AGE_RESTRICTION}/:id`}
					element={
						<SettingsLayoutRoute
							component={AgeRestriction}
							permissions={{ name: "projectSettings.settingsAgeRestriction", type: "view" }}
						/>
					}
				/>
				<Route
					path={`${RoutePath.SETTINGS_LICENSE}/:id`}
					element={
						<SettingsLayoutRoute
							component={Licenses}
							permissions={{ name: "projectSettings.settingsLicense", type: "view" }}
						/>
					}
				/>

				<Route
					path={`${RoutePath.SETTINGS_REFERRAL_ID}/:id`}
					element={
						<SettingsLayoutRoute
							component={ReferralFriend}
							permissions={{ name: "projectSettings.settingsReferAFriend", type: "view" }}
						/>
					}
				/>

				<Route
					path={`${RoutePath.SETTINGS_PRODUCTS}/:type/:id`}
					element={
						<SettingsLayoutRoute
							component={Products}
							permissions={{ name: "projectSettings.settingsProducts", type: "view" }}
						/>
					}
				/>

				<Route
					path={`${RoutePath.THIRD_PARTY_INTEGRATIONS}/:id`}
					element={
						<SettingsLayoutRoute
							component={ThirdPartyIntegrations}
							permissions={{ name: "projectSettings.settings3rdparty", type: "view" }}
						/>
					}
				/>
				<Route
					path={`${RoutePath.SETTINGS_SEO}/:id`}
					element={
						<SettingsLayoutRoute
							component={SeoSetiings}
							permissions={{ name: "projectSettings.settingsSEO", type: "view" }}
						/>
					}
				/>
				<Route
					path={`${RoutePath.SETTINGS_SEO_SOCIAL}/:id`}
					element={
						<SettingsLayoutRoute
							component={SeoSocial}
							permissions={{ name: "projectSettings.settingsSEO", type: "view" }}
						/>
					}
				/>
				{/* <Route path={`${RoutePath.SETTINGS_SEO_SCHEMA_MARKUP}/:id`} element={<SettingsLayoutRoute component={SeoSchema} permissions={{ name: 'projectSettings.settingsSEO', type: 'view' }} />} /> */}
				<Route
					path={`${RoutePath.SETTINGS_SEO_SITEMAP}/:id`}
					element={
						<SettingsLayoutRoute
							component={SeoSitemap}
							permissions={{ name: "projectSettings.settingsSEO", type: "view" }}
						/>
					}
				/>
				<Route
					path={`${RoutePath.SETTINGS_SEO_ROBOTS}/:id`}
					element={
						<SettingsLayoutRoute
							component={SeoRobots}
							permissions={{ name: "projectSettings.settingsSEO", type: "view" }}
						/>
					}
				/>
				<Route
					path={`${RoutePath.ADD_ONS}/:id`}
					element={
						<SettingsLayoutRoute
							component={AddOns}
							permissions={{ name: "projectSettings.settingsAddOns", type: "view" }}
						/>
					}
				/>
				<Route
					path={`${RoutePath.CUSTOM_SCRIPTS}/:type/:id`}
					element={
						<SettingsLayoutRoute
							component={CustomScripts}
							permissions={{
								name: {
									script: "projectSettings.settingsCustomScripts",
									style: "projectSettings.settingsCustomStyles",
								},
								type: "view",
							}}
						/>
					}
				/>
				<Route
					path={`${RoutePath.SETTINGS_DOMAINS}/:id`}
					element={
						<SettingsLayoutRoute
							component={Domains}
							permissions={{ name: "projectSettings.settingsDomains", type: "view" }}
						/>
					}
				/>
				<Route
					path={`${RoutePath.SETTINGS_DOMAINS_INFO}/:id/:nsDomains/*`}
					element={
						<SettingsLayoutRoute
							component={DomainInfo}
							permissions={{ name: "projectSettings.settingsDomains", type: "view" }}
						/>
					}
				/>
				<Route
					path={`${RoutePath.SETTINGS_ADD_DOMAIN}/:id`}
					element={
						<SettingsLayoutRoute
							component={AddDomain}
							permissions={{ name: "projectSettings.settingsDomains", type: "manage" }}
						/>
					}
				/>
				<Route
					path={`${RoutePath.DEV_TOOLS}/:id`}
					element={
						<SettingsLayoutRoute
							component={DevTools}
							permissions={{ name: "projectSettings.settingsAddOns", type: "view" }}
						/>
					}
				/>

				{/* UI Customization Layout */}
				<Route
					path={`${RoutePath.CUSTOMIZE}/:id`}
					element={
						<UICustomizationLayoutRoute
							component={Customize}
							permissions={{ name: "page.uiCustomization", type: "view" }}
						/>
					}
				/>

				<Route path={`${RoutePath.SSO}`} element={<EmptyLayoutRoute component={Sso} />} />
				<Route path="*" element={<EmptyLayoutRoute component={NotFound} />} />
			</Routes>
		</ConfigProvider>
	);
}

export default App;
